import { useId, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { layersAtom } from '@/atoms/layers.atom';
import { landxmlMergeTrackingAtom } from '@/atoms/landxmlMerge-progress.atom';
import { AnalysisDialogShell } from '@/components/analyses/AnalysisDialogShell';
import { AnalysisSection, OutputSummary } from '@/components/analyses/sections';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { execute } from '@/graphql/execute';
import { isReadyLandxmlMergeSource } from '@/lib/analyses/candidates';
import { useInvalidateAnalysisRuns } from '@/lib/analyses/useAnalysisRuns';
import { useDialog } from '@/providers/DialogProvider';
import { startLandxmlMerge } from '@/queries/landxmlMerge.query';

interface Props {
  projectId: string;
  initialLayerId?: string;
  initialSourceLayerIds?: string[];
  initialName?: string;
  missingInputs?: boolean;
}

export default function LandxmlMergeDialog({ projectId, initialLayerId, initialSourceLayerIds, initialName, missingInputs }: Props) {
  const { t } = useTranslation('project');
  const formId = useId();
  const allLayers = useAtomValue(layersAtom);
  const setTrackers = useSetAtom(landxmlMergeTrackingAtom);
  const invalidateRuns = useInvalidateAnalysisRuns();
  const { dismissActiveDialog } = useDialog();
  const [name, setName] = useState(initialName ?? '');
  const [selectedIds, setSelectedIds] = useState<string[]>(
    () => [...new Set(initialSourceLayerIds ?? (initialLayerId ? [initialLayerId] : []))],
  );
  const candidates = useMemo(
    () => allLayers.filter(isReadyLandxmlMergeSource)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [allLayers],
  );
  const chosen = selectedIds.map((id) => candidates.find((layer) => layer.id === id))
    .filter((layer): layer is (typeof candidates)[number] => !!layer);
  const canSubmit = chosen.length >= 2 && chosen.length <= 20 && name.trim().length >= 1 && name.trim().length <= 120;
  const mutation = useMutation({
    mutationFn: () => execute(startLandxmlMerge, {
      input: { projectId, sourceLayerIds: chosen.map((layer) => layer.id), name: name.trim() },
    }),
    onSuccess: (data) => {
      invalidateRuns(projectId);
      const toastId = toast.loading(t('landxmlMerge.running', { name: name.trim() }), { duration: Infinity });
      setTrackers((current) => [...current, { projectId, batchId: data.startLandxmlMerge, toastId, outputName: name.trim() }]);
      dismissActiveDialog();
    },
    onError: (error: Error) => toast.error(error.message || t('landxmlMerge.failed')),
  });

  return (
    <AnalysisDialogShell title={t('landxmlMerge.title')} description={t('landxmlMerge.description')}
      formId={formId} primaryLabel={t('landxmlMerge.merge')} primaryBusy={mutation.isPending}
      primaryDisabled={!canSubmit || mutation.isPending}>
      <form id={formId} className='flex flex-col gap-4 px-1 py-2' onSubmit={(event) => {
        event.preventDefault();
        if (canSubmit && !mutation.isPending) mutation.mutate();
      }}>
        <AnalysisSection title={t('analysisConfig.sections.inputs')}>
          {missingInputs && <p role='status' className='text-amber-700'>{t('landxmlMerge.missingInputs')}</p>}
          <p className='text-sm text-muted-foreground'>{t('landxmlMerge.selectHint')}</p>
          <div className='max-h-64 space-y-2 overflow-y-auto'>
            {candidates.map((layer) => (
              <label key={layer.id} className='flex items-center gap-2 rounded-md border px-3 py-2'>
                <Checkbox checked={selectedIds.includes(layer.id)} aria-label={layer.name} onCheckedChange={(checked) => {
                  setSelectedIds((previous) => {
                    if (checked === true) return previous.includes(layer.id) ? previous : [...previous, layer.id];
                    return previous.filter((id) => id !== layer.id);
                  });
                }} />
                <span className='truncate'>{layer.name}</span>
              </label>
            ))}
          </div>
          {candidates.length < 2 && <p className='text-sm text-muted-foreground'>{t('landxmlMerge.needTwo')}</p>}
        </AnalysisSection>
        <AnalysisSection title={t('analysisConfig.sections.output')}>
          <Label htmlFor={`${formId}-name`}>{t('landxmlMerge.resultName')}</Label>
          <Input id={`${formId}-name`} value={name} maxLength={120} onChange={(event) => setName(event.target.value)} />
          <OutputSummary creates={[t('landxmlMerge.outputSummary', { count: chosen.length })]} />
        </AnalysisSection>
      </form>
    </AnalysisDialogShell>
  );
}
